import { ReactElement, useState, useEffect } from 'react';
import { PlaceType } from './LocationAutocomplete';
import NewLocationAutocomplete from './NewLocationAutocomplete';
import CTA from './Cta';
import { IFragmentCta } from 'types/contentful';
import RichText from './Richtext';
import type { Document } from '@contentful/rich-text-types';

interface SearchHeroProps {
  id: string;
  heading: string;
  subHeading?: string | undefined;
  description?: Document | undefined;
  ctA?: IFragmentCta[] | undefined;
  backgroundColor?: 'Default - white' | 'Muted' | undefined;
  locationSelected(place: PlaceType | null): void;
  onUseMyLocation(coords: GeolocationCoordinates): void;
  initValue?: PlaceType | null;
  freshPaintToken: string | null | undefined;
  className?: string;
  headingType?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | undefined;
}

export default function SearchBar({
  locationSelected,
  onUseMyLocation,
  initValue,
  freshPaintToken,
  className,
  description,
  ctA,
  heading,
  subHeading,
  backgroundColor,
  headingType,
}: SearchHeroProps): ReactElement {
  const [background, setBackground] = useState<string>('bg-white');

  useEffect(() => {
    if (backgroundColor && backgroundColor === 'Muted') {
      setBackground('bg-muted');
    }
  }, [backgroundColor]);

  return (
    <div
      className={`md:px-10 md:py-20 md:pb-16 text-center space-y-6 text-primary-actual ${className} ${background}`}
    >
      <div>
        {(() => {
          const Tag = (headingType || 'h5') as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
          return (
            <Tag className="text-3xl lg:text-5xl font-light text-primary-actual lg:inline md:mr-2">
              {heading}
              {subHeading && (
                <>
                  <span className="text-3xl lg:text-5xl mt-2 font-heading bold lg:inline">
                    &nbsp; {subHeading}
                  </span>
                </>
              )}
            </Tag>
          );
        })()}
      </div>
      <div className="flex flex-col w-full md:w-3/4 mx-auto space-y-6">
        {
          <NewLocationAutocomplete
            className={`${background}`}
            onSelect={locationSelected}
            onUseMyLocation={onUseMyLocation}
            initValue={initValue}
            freshPaintToken={freshPaintToken}
            hideEndAdornment={true}
          />
        }
        {description && (
          <RichText
            document={description}
            classNames="text-primary-actual text-lg font-light px-2"
          />
        )}
      </div>
      <div className="flex flex-row justify-between w-full sm:w-4/5 md:w-3/5 lg:w-1/2 sm:mx-auto">
        {ctA?.map((x, index) => (
          <div key={index} className="w-button mx-1">
            <CTA
              {...x.fields}
              classNames=" w-full flex flex-grow justify-center text-primary-actual button-text bg-white remove-cta-padding"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
